import { AlertOptions, useAppAlert } from '@/app/AppAlertProvider'
import DocumentPreviewNew from '@/app/common/components/Documents/DocumentPreviewNew'
import DocumentUploadNew from '@/app/common/components/Documents/DocumentUploadNew'
import { MultipleErrorsSchema } from '@/app/common/types_and_interfaces'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { MutationStatus } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import FamilyWizardLayout from '../family_application/FamilyWizardLayout'
import { submitDocument } from '../family_application/residency_verification/hooks/useUploadFile'
import { usePostSpecialEducationDocument } from '../family_application/student_details/hooks/usePostSpecialEducationDocument'
import { Status } from '@/common/constants.ts'

const DocumentType = {
  identity:1,
  income:2,
  student:3
} as const

const DocumentVariationId = {
  special_education:0,
  identity:2,
  residency_card:4,
  birth_certificate:6,
  tax_form_1040:7,
  other:9,
  student_portfolio:17,
} as const

const DocumentTypeOptions = [
  {
    variation: DocumentVariationId.identity,
    name: 'identity',
    code: 'identity_verification',
    requireStudent: false,
  },
  {
    variation: DocumentVariationId.residency_card,
    name: 'residency',
    code: 'residency_verification',
    requireStudent: false,
  },
  {
    variation: DocumentVariationId.birth_certificate,
    name: 'student_age',
    code: 'student_age',
    requireStudent: true,
  },
  {
    variation: DocumentVariationId.special_education,
    name: 'special_education',
    code: 'special_education',
    requireStudent: true,
  },
  /* {
    variation: 0,
    name: 'military_exemption',
    code: 'military_exemption'
  }, */
  {
    variation: DocumentVariationId.tax_form_1040,
    name: 'income',
    code: 'income_verification',
    requireStudent: false,
  },
  {
    variation: DocumentVariationId.student_portfolio,
    name:'portfolio',
    code:'student_achievement',
    requireStudent:true
  },
  {
    variation: DocumentVariationId.other,
    name: 'other',
    code: 'other',
    requireStudent: true,
  },

] as const

function getStudentVariationIds(){
  return DocumentTypeOptions.filter(documentType => documentType.requireStudent).map(documentType => documentType.variation)
}

function DocumentsPage() {
  const { t: translate } = useTranslation()
  const application = useSelector(selectFamilyApplication)
  const { reset, register, watch, setValue } = useForm()
  const student = watch('student')
  const rawSelectedDocOption = watch('document_type')
  const selectedDocOption = rawSelectedDocOption ? JSON.parse(rawSelectedDocOption) : ''
  const files = watch('files')
  const programId = useSelector(selectProgramID)
  const { fireAlert } = useAppAlert()
  const navigateTo = useNavigate()
  const [status, setStatus] = useState<MutationStatus>('idle')


  const specialEducationMutation = usePostSpecialEducationDocument()

  const onSubmit = async () => {
    setStatus('pending')
    const studentRecord = student ? JSON.parse(student) : null
    if (selectedDocOption.variation != DocumentVariationId.special_education) {
      const form = new FormData()
      form.append('file', files[0])
      form.append('variation', String(selectedDocOption.variation))
      form.append('program_id', String(programId))
      form.append('from_document_module', String(1))
      if (getStudentVariationIds().includes(selectedDocOption.variation)) {
        if(studentRecord == undefined || studentRecord == null){
          await fireAlert({
            variant: 'error',
            message: translate('no_student_selected')
          })
          setStatus('idle')
          reset()
          return
        }
        form.append('student_id', studentRecord.id)
        form.append('type', String(DocumentType.student))
      } else if ([DocumentVariationId.tax_form_1040].includes(selectedDocOption.variation)) {
        form.append('type', String(DocumentType.income))
      } else {
        form.append('type', String(DocumentType.identity))
      }

      const response = await submitDocument(form)
      const multipleErrors = MultipleErrorsSchema.safeParse(
        response.error?.details?.errorsMessage
      )

      if (response.error) {
        const alertConfig: AlertOptions = {
          variant: 'error',
          titleText:
            response.error.message ??
            translate('an_error_occurred_whil_processing_the_document'),
          title: true,
          denyBtn: true,
          denyBtnText: translate('retry'),
          messageComponent: (
            <ul className="text-sm space-y-2">
              {multipleErrors.success &&
                multipleErrors.data.map((item) => (
                  <li className="list-disc list-inside text-left" key={item}>
                    {item}
                  </li>
                ))}
            </ul>
          ),
        }

        fireAlert(alertConfig).then(({ denied }) => {
          if (denied) {
            navigateTo(-1)
          }
        })
      } else {


        fireAlert({
          variant: 'success',
          titleText: translate('success'),
          message: translate('document_submitted'),
        }).then(() => {
          reset()
        })
      }
    } else if (rawSelectedDocOption.name === 'special_education') {
      if(studentRecord == undefined || studentRecord == null){
        await fireAlert({
          variant: 'error',
          message: translate('no_student_selected')
        })
        setStatus('idle')
        reset()
        return
      }
      const form = new FormData()
      form.append('file', files)
      form.append('document_type', String(5))
      form.append('application_id', String(application.application_id))
      form.append('student_id', studentRecord.id)
      form.append('from_document_module', String(Status.Yes))
      specialEducationMutation.mutate(form, {
        onSuccess: () => {
          fireAlert({
            variant: 'success',
            titleText: translate('success'),
            message: translate('document_submitted'),
          }).then(() => {
            reset()
          })
        },
        onError: ({ message }) => {
          fireAlert({
            variant: 'error',
            denyBtn: true,
            denyBtnText: translate('retry'),
            message,
          }).then(({ denied }) => {
            if (denied) {
              reset()
            }
          })
        },
      })
    }
    setStatus('idle')
  }

  if (
    watch('files') &&
    rawSelectedDocOption &&
    (!selectedDocOption.requireStudent || (selectedDocOption.requireStudent && student))
  ) {
    return (
      <div className="flex items-center justify-center max-w-2xl mx-auto ">
        <DocumentPreviewNew
          status={status}
          file={files[0]}
          onContinueClick={() => {
            console.log('here')
            onSubmit()
          }}
          onRetakeClick={() => {
            reset()
          }}
        />
      </div>
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        {translate('document_upload')}
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <div className="flex-grow flex flex-col max-w-md w-full mx-auto">
          <div className="flex flex-col mt-6 space-y-2">
            <label
              htmlFor="state"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('what_is_this_document_for')}
            </label>
            <div>
              <select
                {...register('document_type')}
                className="capitalize block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
              >
                <option className="hidden" value="">
                  {translate('select_one')}
                </option>
                {DocumentTypeOptions.map((option, index) => {
                  return (
                    <option
                      className="capitalize"
                      key={index}
                      value={JSON.stringify(option)}
                    >
                      {translate(option.name).charAt(0).toUpperCase() +
                        translate(option.name).slice(1)}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
          {selectedDocOption.requireStudent && (
            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="state"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('who_is_this_document_for')}
                <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <select
                  {...register('student')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                >
                  <option className="hidden" value="">
                    {translate('select_one')}
                  </option>
                  {application?.students.map((student) => {
                    return (
                      <option key={student.id} value={JSON.stringify(student)}>
                        {`${student.first_name} ${student.last_name}`} (Student)
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>
          )}
          <div className='mt-4'>
            <DocumentUploadNew
              onUpload={(document) => {
                setValue('files', document)
              }}
              isPdfAllowed={selectedDocOption.variation === DocumentVariationId.student_portfolio}
            />
          </div>
        </div>
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}

export default DocumentsPage
